import {
  APIFieldType,
  Operator,
  ReportColumnType,
  SupportedDashboardFilterReferenceType,
} from '@ardoq/api-types';
import { UNSET_OR_NOTAPPLICABLE_VALUE_LABEL } from '../../consts';
import { isEmpty, keyBy } from 'lodash';
import type {
  DashboardFilterOperatorButtonProps,
  DashboardFilterPillPopoverContentProps,
  DashboardFilterPillsUIStateWithSetters,
  DashboardFilterPillValueProps,
} from '@ardoq/table-filter-bar';
import { type I18nLocale, t } from '@ardoq/i18n';
import type {
  DashboardFilterWithHierarchicalSuggestions,
  DashboardFilterWithSuggestions,
} from '../../types';
import { isReportComponentIdValueColumn } from '../../utils';
import { flattenHierarchy } from '../hierarchicalFilterSuggestions.utils';
import { toValuePopoverContentProps } from '../toValuePopoverContentProps';
import {
  type GetOperatorAndValuePropsParams,
  getValueProps,
  getListPopoverPropsWithFilter,
  getHierarchicalListPopoverPropsWithFilter,
} from './utils';

type ListWithSuggestionsParams = GetOperatorAndValuePropsParams & {
  dashboardFilter: DashboardFilterWithSuggestions;
};

type HierarchicalListWithSuggestionsParams = GetOperatorAndValuePropsParams & {
  dashboardFilter: DashboardFilterWithHierarchicalSuggestions;
};

type OperatorAndValuePropsResult = {
  operatorProps: DashboardFilterOperatorButtonProps;
  valueProps: DashboardFilterPillValueProps;
};

const getOperatorPropsAndFilterBase = (params: ListWithSuggestionsParams) => {
  const {
    filterPillUIState,
    dashboardFilter,
    updateDashboardFilterValues,
    i18nLocale,
  } = params;

  const operatorUIStateProps = {
    button: {
      isPopoverOpen: filterPillUIState.isOperatorPopoverOpen,
      onClick: filterPillUIState.toggleIsOperatorPopoverOpen,
    },
    popover: {
      isOpen: filterPillUIState.isOperatorPopoverOpen,
      onClose: filterPillUIState.toggleIsOperatorPopoverOpen,
    },
  };

  const filterType = dashboardFilter.type;

  const selectedOptions = dashboardFilter.values.map(filterExpression =>
    filterExpression.value === null
      ? {
          value: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
          label: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
        }
      : {
          value: String(filterExpression.value),
          label: filterExpression.label ?? String(filterExpression.value),
        }
  );

  const operator =
    dashboardFilter.values.find(
      expression => expression.operator !== Operator.IS_EMPTY
    )?.operator ?? Operator.EQUAL;

  const isTextTypeFilter =
    dashboardFilter.type === APIFieldType.TEXT &&
    !isReportComponentIdValueColumn(dashboardFilter.field.name);

  const operatorLabel =
    isTextTypeFilter && operator === Operator.CONTAINS_SUBSTRING
      ? t(i18nLocale, 'dashboard:filter.operator.contains', 'contains')
      : dashboardFilter.values.length > 1
        ? t(i18nLocale, 'dashboard:filter.operator.isAnyOf', 'is any of')
        : t(i18nLocale, 'dashboard:filter.operator.is', 'is');

  const operatorProps: DashboardFilterOperatorButtonProps = isTextTypeFilter
    ? {
        button: {
          ...operatorUIStateProps.button,
          label: operatorLabel,
        },
        popover: {
          ...operatorUIStateProps.popover,
          options: [Operator.EQUAL, Operator.CONTAINS_SUBSTRING].map(
            operator => ({
              value: operator,
              label:
                operator === Operator.EQUAL
                  ? t(i18nLocale, 'dashboard:filter.operator.is', 'is')
                  : t(
                      i18nLocale,
                      'dashboard:filter.operator.contains',
                      'contains'
                    ),
              onClick: () => {
                updateDashboardFilterValues(
                  dashboardFilter.id,
                  filterType,
                  isEmpty(dashboardFilter.values)
                    ? [{ value: '', operator }] // FIXME: hack to set the operator without having selected a value first. We should make it possible change the operator independently
                    : dashboardFilter.values
                        // filter out IS_EMPTY (null) values when switching to contains, as "Not Set or N/A" is not valid for contains
                        .filter(
                          ({ value }) =>
                            !(
                              operator === Operator.CONTAINS_SUBSTRING &&
                              value === null
                            )
                        )
                        .map(({ value }) => ({
                          value,
                          operator,
                        }))
                );
                filterPillUIState.toggleIsOperatorPopoverOpen();
              },
            })
          ),
        },
      }
    : { hide: true };

  return {
    filterType,
    selectedOptions,
    operator,
    operatorProps,
  };
};

const buildOnValueChange = (
  params: ListWithSuggestionsParams,
  filterType:
    | APIFieldType.USER
    | APIFieldType.TEXT
    | SupportedDashboardFilterReferenceType,
  operator: Operator,
  allOptionsByValue: Record<string, { label?: string }>
) => {
  const { dashboardFilter, updateDashboardFilterValues } = params;
  return (values: string[]) =>
    updateDashboardFilterValues(
      dashboardFilter.id,
      filterType,
      values.map(v => {
        if (v === UNSET_OR_NOTAPPLICABLE_VALUE_LABEL) {
          return {
            operator: Operator.IS_EMPTY,
            value: null,
          };
        }
        // for id value types, we need to set the label to display
        if (
          filterType === APIFieldType.USER ||
          (filterType === APIFieldType.TEXT &&
            isReportComponentIdValueColumn(dashboardFilter.field.name)) ||
          filterType === ReportColumnType.REFERENCE_TYPE_INCOMING ||
          filterType === ReportColumnType.REFERENCE_TYPE_OUTGOING
        ) {
          return {
            operator: Operator.EQUAL,
            value: v,
            label: allOptionsByValue[v]?.label,
          };
        }

        return {
          operator,
          value: v,
        };
      })
    );
};

type BuildValuePropsParams = {
  filterPillUIState: DashboardFilterPillsUIStateWithSetters;
  dashboardFilter: DashboardFilterWithSuggestions;
  hasInaccessibleWidgets: boolean;
  requestDashboardFilterSuggestions: (query: string) => void;
  onValueChange: (values: string[]) => void;
  content: DashboardFilterPillPopoverContentProps;
  i18nLocale: I18nLocale;
};

const buildValueProps = ({
  filterPillUIState,
  dashboardFilter,
  hasInaccessibleWidgets,
  requestDashboardFilterSuggestions,
  onValueChange,
  content,
  i18nLocale,
}: BuildValuePropsParams): DashboardFilterPillValueProps => {
  return getValueProps({
    filterPillUIState,
    i18nLocale,
    name: dashboardFilter.name,
    labelsForSelectedValues: dashboardFilter.values
      .filter(({ value }) => value !== '') // FIXME: For text filter may have '' if the operator is changed without having selected value first
      .map(({ value, label }) => {
        if (value === null) {
          return UNSET_OR_NOTAPPLICABLE_VALUE_LABEL;
        }
        return label ?? String(value);
      }),
    content: toValuePopoverContentProps(content, filterPillUIState, i18nLocale),
    hasLinkedWidgets: dashboardFilter.linkCounts.total > 0,
    isInvalid: false,
    hasInaccessibleWidgets,
    clearFilter: () => onValueChange([]),
    onClick: () => {
      if (!filterPillUIState.isValuePopoverOpen) {
        // reset the suggestion query and trigger the load of suggestions for next time opening the popover
        filterPillUIState.setPopoverInputValue('');
        requestDashboardFilterSuggestions('');
      }
      filterPillUIState.toggleIsValuePopoverOpen();
    },
    onClose: filterPillUIState.toggleIsValuePopoverOpen,
  });
};

export const getFlatListWithSuggestionsOperatorAndValueProps = (
  params: ListWithSuggestionsParams
): OperatorAndValuePropsResult => {
  const { dashboardFilter, requestDashboardFilterSuggestions } = params;

  const { filterType, selectedOptions, operator, operatorProps } =
    getOperatorPropsAndFilterBase(params);

  const suggestionsQuery = dashboardFilter.suggestionProps?.query;
  const isContainsOperator = operator === Operator.CONTAINS_SUBSTRING;
  const isTextTypeFilter =
    dashboardFilter.type === APIFieldType.TEXT &&
    !isReportComponentIdValueColumn(dashboardFilter.field.name);

  const apiSuggestions = dashboardFilter.suggestionProps?.suggestions ?? [];

  const allOptionsByValue = keyBy(
    [
      // only show the UNSET_OR_NOTAPPLICABLE_VALUE_LABEL when there is no query or the query matches it
      // and the operator is not CONTAINS_SUBSTRING for text filters (not set/n/a is not valid for contains)
      ...(!isContainsOperator &&
      (!suggestionsQuery ||
        UNSET_OR_NOTAPPLICABLE_VALUE_LABEL.toLowerCase().includes(
          suggestionsQuery.toLowerCase()
        ))
        ? [
            {
              label: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
              value: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
            },
          ]
        : []),
      ...selectedOptions.filter(
        ({ value }) =>
          !suggestionsQuery ||
          value.toLowerCase().includes(suggestionsQuery.toLowerCase())
      ),
      ...apiSuggestions,
    ],
    'value'
  );
  const onValueChange = buildOnValueChange(
    params,
    filterType,
    operator,
    allOptionsByValue
  );

  return {
    operatorProps,
    valueProps: buildValueProps({
      filterPillUIState: params.filterPillUIState,
      dashboardFilter: params.dashboardFilter,
      hasInaccessibleWidgets: params.hasInaccessibleWidgets,
      requestDashboardFilterSuggestions: (query: string) =>
        requestDashboardFilterSuggestions(dashboardFilter.id, query),
      onValueChange,
      i18nLocale: params.i18nLocale,
      content: getListPopoverPropsWithFilter({
        options: Object.values(allOptionsByValue),
        values: selectedOptions
          .map(({ value }) => value) // FIXME: For text filter may have '' if the operator is changed without having selected value first
          .filter(Boolean),
        onValueChange,
        dashboardFilterSuggestionsProps: dashboardFilter.suggestionProps,
        requestDashboardFilterSuggestions: (query: string) =>
          requestDashboardFilterSuggestions(dashboardFilter.id, query),
        includeFreeTextOption: isTextTypeFilter,
        i18nLocale: params.i18nLocale,
        filterInputPlaceholder: params.filterInputPlaceholder,
      }),
    }),
  };
};

export const getHierarchicalListWithSuggestionsOperatorAndValueProps = (
  params: HierarchicalListWithSuggestionsParams
): OperatorAndValuePropsResult => {
  const {
    dashboardFilter,
    requestDashboardFilterHierarchicalSuggestions,
    filterCachedHierarchicalSuggestions,
  } = params;

  const { filterType, selectedOptions, operator, operatorProps } =
    getOperatorPropsAndFilterBase(params);

  const suggestions = dashboardFilter.suggestionProps?.suggestions ?? [];

  const allOptionsByValue = keyBy(
    [
      {
        label: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
        value: UNSET_OR_NOTAPPLICABLE_VALUE_LABEL,
      },
      ...selectedOptions,
      ...flattenHierarchy(suggestions),
    ],
    'value'
  );
  const onValueChange = buildOnValueChange(
    params,
    filterType,
    operator,
    allOptionsByValue
  );

  const requestDashboardFilterSuggestionsFn = (query: string) =>
    requestDashboardFilterHierarchicalSuggestions(dashboardFilter.id, query);

  const filterCachedHierarchicalSuggestionsFn = (query: string) =>
    filterCachedHierarchicalSuggestions(dashboardFilter.id, query);

  return {
    operatorProps,
    valueProps: buildValueProps({
      filterPillUIState: params.filterPillUIState,
      dashboardFilter: params.dashboardFilter,
      hasInaccessibleWidgets: params.hasInaccessibleWidgets,
      requestDashboardFilterSuggestions: requestDashboardFilterSuggestionsFn,
      onValueChange,
      i18nLocale: params.i18nLocale,
      content: getHierarchicalListPopoverPropsWithFilter({
        options: suggestions,
        selectedOptions,
        onValueChange,
        dashboardFilterSuggestionsProps: dashboardFilter.suggestionProps,
        requestDashboardFilterSuggestions: requestDashboardFilterSuggestionsFn,
        filterCachedHierarchicalSuggestions:
          filterCachedHierarchicalSuggestionsFn,
        i18nLocale: params.i18nLocale,
        filterInputPlaceholder: params.filterInputPlaceholder,
      }),
    }),
  };
};
